import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { AdminStats, formatBytes } from 'hooks/useAdminStats';
import { ui } from 'util/ui';
import { Panel, SectionHead, TRow, Cell } from './adminUi';

/** Admin › Overview. UI refresh styling (5 Nov 2026): rounded panels and stacking tables. */

const CATEGORY_ORDER = ['master', 'preview', 'distribution', 'CAD'];
const CATEGORY_LABEL: Record<string, string> = {
  master: 'Master',
  preview: 'Preview',
  distribution: 'Distribution',
  CAD: 'Approval',
};

const statGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 };

/** A figure. With `onOpen` it becomes a button that opens the matching filtered project list. */
const Stat: FC<{ label: string; value: string | number; sub?: React.ReactNode; accent?: string; onOpen?(): void }> = ({
  label,
  value,
  sub,
  accent,
  onOpen,
}) => {
  const body = (
    <>
      <div style={{ fontSize: 12, fontWeight: 600, color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || ui.ink, marginTop: 6, letterSpacing: '-0.01em' }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div style={{ fontSize: 13, color: ui.muted, marginTop: 4 }}>{sub}</div>}
      {onOpen && <div style={{ fontSize: 13, fontWeight: 600, color: ui.link, marginTop: 8 }}>Show and fix →</div>}
    </>
  );
  const box: React.CSSProperties = {
    background: ui.surface,
    border: `1px solid ${ui.line}`,
    borderRadius: 16,
    padding: 16,
    textAlign: 'left',
    fontFamily: ui.font,
    color: ui.ink,
  };
  if (!onOpen) return <div style={box}>{body}</div>;
  return (
    <button type="button" className="archy-btn archy-card-btn" onClick={onOpen} title="Show these projects" style={{ ...box, cursor: 'pointer', width: '100%' }}>
      {body}
    </button>
  );
};

const Section: FC<{ title: string; note?: string }> = ({ title, note, children }) => (
  <section style={{ marginBottom: 28 }}>
    <SectionHead title={title} note={note} />
    {children}
  </section>
);

const Overview: FC<{ stats: AdminStats }> = ({ stats }) => {
  const dispatch = useDispatch();
  const pct = (n: number) => (stats.projects.total ? Math.round((n / stats.projects.total) * 100) : 0);
  const categories = [
    ...CATEGORY_ORDER.filter((c) => stats.videos.byCategory[c]),
    ...Object.keys(stats.videos.byCategory).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];
  // Storage tier follows the label since 5 Nov 2026: Preview is hot, every other label is cold.
  const coldBytes = Object.keys(stats.videos.byCategory)
    .filter((c) => c !== 'preview')
    .reduce((sum, c) => sum + (stats.videos.byCategory[c].bytes || 0), 0);

  return (
    <>
      <Section title="Projects">
        <div style={statGrid}>
          <Stat label="Total projects" value={stats.projects.total} />
          <Stat label="Complete" value={stats.projects.complete} sub={`${pct(stats.projects.complete)}% of projects`} accent={ui.complete} />
          <Stat label="Incomplete" value={stats.projects.incomplete} sub={`${pct(stats.projects.incomplete)}% of projects`} accent={ui.incomplete} />
          <Stat label="Records" value={stats.records.total} sub={`${stats.notes.toLocaleString()} notes`} />
        </div>
      </Section>

      <Section title="Storage" note="Calculated from the file sizes stored on each database record">
        <div style={{ ...statGrid, marginBottom: 14 }}>
          {/* Hot and cold on two short lines (Aaron, 5 Nov 2026). */}
          <Stat
            label="Total stored"
            value={formatBytes(stats.totalBytes)}
            accent={ui.accent}
            sub={
              <span style={{ display: 'flex', flexDirection: 'column', gap: 1, lineHeight: '17px' }}>
                <span style={{ color: '#E5056A', fontWeight: 600 }} title="Previews, project assets and gallery: hot storage (Standard)">
                  ● Hot {formatBytes(Math.max(0, stats.totalBytes - coldBytes))}
                </span>
                <span style={{ color: '#1E4E8C', fontWeight: 600 }} title="Master, Approval and Distribution files: cold storage (Archive)">
                  ● Cold {formatBytes(coldBytes)}
                </span>
              </span>
            }
          />
          <Stat label="Video files" value={stats.videos.total} sub={formatBytes(stats.videos.bytes)} />
          <Stat label="Project assets" value={stats.assets.files} sub={formatBytes(stats.assets.bytes)} />
          <Stat label="Gallery" value={stats.gallery.files} sub={formatBytes(stats.gallery.bytes)} />
          <Stat label="Images" value={stats.images} sub="sizes not recorded" />
        </div>
        <Panel>
          <TRow head columns="minmax(0,3fr) 1fr 1fr 1fr">
            <Cell>Video files by label</Cell>
            <Cell align="right">Files</Cell>
            <Cell align="right">Size</Cell>
            <Cell align="right">Share</Cell>
          </TRow>
          {categories.map((c) => {
            const s = stats.videos.byCategory[c];
            return (
              <TRow key={c} columns="minmax(0,3fr) 1fr 1fr 1fr">
                <Cell style={{ fontWeight: 600 }}>{CATEGORY_LABEL[c] || c}</Cell>
                <Cell label="Files" align="right">{s.files.toLocaleString()}</Cell>
                <Cell label="Size" align="right">{formatBytes(s.bytes)}</Cell>
                <Cell label="Share" align="right">
                  {`${stats.videos.bytes ? Math.round((s.bytes / stats.videos.bytes) * 100) : 0}%`}
                </Cell>
              </TRow>
            );
          })}
        </Panel>
      </Section>

      <Section title="Largest projects" note="By storage used. Click one to open it.">
        <Panel>
          <TRow head columns="minmax(0,4fr) 1fr 1fr">
            <Cell>Project</Cell>
            <Cell align="right">Files</Cell>
            <Cell align="right">Size</Cell>
          </TRow>
          {stats.topProjects.map((p) => (
            <TRow key={p.id} columns="minmax(0,4fr) 1fr 1fr" onOpen={() => dispatch(push(`/project/${p.id}`))} title="Open this project">
              <Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong style={{ fontWeight: 600 }}>{p.title}</strong>
                {p.client ? <span style={{ color: ui.muted }}> · {p.client}</span> : null}
              </Cell>
              <Cell label="Files" align="right">{p.files.toLocaleString()}</Cell>
              <Cell label="Size" align="right">{formatBytes(p.bytes)}</Cell>
            </TRow>
          ))}
        </Panel>
      </Section>

      <Section title="Needs attention" note="Click one to open that list of projects and work through it">
        <div style={statGrid}>
          <Stat label="Incomplete projects" value={stats.projects.incomplete} accent={ui.incomplete} onOpen={() => dispatch(push('/?attention=incomplete'))} />
          <Stat label="Projects with no client" value={stats.projects.noClient} onOpen={() => dispatch(push('/?attention=noClient'))} />
          <Stat
            label="Records with no video files"
            value={stats.records.withoutFiles}
            sub="Opens the projects they belong to"
            onOpen={() => dispatch(push('/?attention=emptyRecords'))}
          />
        </div>
      </Section>
    </>
  );
};

export default Overview;
